import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  ShieldCheck,
} from "lucide-react";
import "./WorkShowcase.css";

const projects = [
  {
    number: "01",
    title: "Thwala Attorneys Inc.",
    category: "LEGAL OPERATIONS PLATFORM",
    description: "A modern legal website focused on consultation booking, service discovery, and secure client enquiries.",
    tags: ["Consultation Booking", "Secure Enquiries", "Legal Services"],
    accent: "#27d9ff",
    crop: { x: 52, y: 515, width: 493, height: 237 },
    previewSrc: "/work-thwala.png",
    url: "https://thwalaattorneys.co.za/",
  },
  {
    number: "02",
    title: "Luna Rides",
    category: "LUXURY MOBILITY PLATFORM",
    description: "A premium mobility platform connecting riders with professional chauffeurs for a seamless, elevated travel experience.",
    tags: ["Real-time Tracking", "Payment Integration", "Driver Management"],
    accent: "#b54cff",
    crop: { x: 610, y: 515, width: 449, height: 237 },
    previewSrc: undefined,
    url: "https://luna-rides.onrender.com/",
  },
  {
    number: "03",
    title: "ShuttleBee",
    category: "SCHOOL TRANSPORT PLATFORM",
    description: "A smart school transport platform that keeps learners safe with live tracking, parent updates, and route management.",
    tags: ["Live Tracking", "Parent Notifications", "Route Optimisation"],
    accent: "#22d8ff",
    crop: { x: 1111, y: 515, width: 506, height: 237 },
    previewSrc: "/work-shuttlebee-premium-v2.png",
    url: "https://shuttle-bee.onrender.com/",
  },
];

function ProjectPreview({
  crop,
  title,
  previewSrc,
}: {
  crop: { x: number; y: number; width: number; height: number };
  title: string;
  previewSrc?: string;
}) {
  return (
    <div
      className="edx-work-preview"
      role="img"
      aria-label={`${title} website homepage`}
      style={{ "--preview-aspect": `${crop.width} / ${crop.height}` } as CSSProperties}
    >
      {previewSrc ? (
        <img className="edx-work-preview-image" src={previewSrc} alt="" aria-hidden="true" />
      ) : (
        <svg viewBox={`0 0 ${crop.width} ${crop.height}`} preserveAspectRatio="xMidYMid meet" aria-hidden="true">
          <image href="/edenox-work-reference-exact.png" x={-crop.x} y={-crop.y} width="1672" height="941" />
        </svg>
      )}
      <span className="edx-work-preview-glow" aria-hidden="true" />
    </div>
  );
}

export default function WorkShowcase() {
  const sectionRef = useRef<HTMLElement>(null);
  const [activeProject, setActiveProject] = useState(0);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => document.body.classList.toggle("home-work-showcase-active", entry.isIntersecting),
      { threshold: 0.42 },
    );

    observer.observe(section);
    return () => {
      observer.disconnect();
      document.body.classList.remove("home-work-showcase-active");
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      id="our-work"
      data-testid="work-showcase"
      className="edx-work-showcase"
      aria-label="Work that turns bold ideas into real progress."
      style={{ "--active-accent": projects[activeProject].accent } as CSSProperties}
    >
      <div className="edx-work-grid" aria-hidden="true" />
      <div className="edx-work-ambient" aria-hidden="true" />

      <div className="edx-work-frame">
        <img
          className="edx-work-reference-board"
          src="/edenox-work-reference-exact.png"
          alt=""
          aria-hidden="true"
        />
        <div className="edx-work-reference-previews" aria-hidden="true">
          <img className="edx-work-reference-preview edx-work-reference-preview-thwala" src="/work-thwala.png" alt="" />
          <img className="edx-work-reference-preview edx-work-reference-preview-shuttlebee" src="/work-shuttlebee-premium-v2.png" alt="" />
        </div>
        <div className="edx-work-reference-hotspots" aria-label="Work showcase links">
          <Link className="edx-work-hotspot edx-work-hotspot-see" to="/case-studies" aria-label="See our work" />
          <Link className="edx-work-hotspot edx-work-hotspot-talk" to="/mission-control" aria-label="Talk to us" />
          {projects.map((project, index) => (
            <a
              key={project.title}
              className={`edx-work-hotspot edx-work-hotspot-project edx-work-hotspot-project-${index + 1}`}
              href={project.url}
              target="_blank"
              rel="noreferrer"
              aria-label={`Visit ${project.title} website`}
            />
          ))}
        </div>

        <div className="edx-work-intro">
          <div className="edx-work-wordmark">EDENOX</div>
          <div className="edx-work-kicker">
            <span>OUR WORK.01</span><i /><b>REAL PRODUCTS. REAL IMPACT.</b>
          </div>
          <h2 id="work-showcase-heading">
            Work that turns<br />bold ideas into<br /><span>real progress.</span>
          </h2>
          <p>EDENOX builds digital products, intelligent systems, and scalable platforms that solve real business problems.</p>
          <div className="edx-work-cta-row">
            <Link to="/case-studies" className="edx-work-primary">See our work <ArrowRight /></Link>
            <Link to="/mission-control" className="edx-work-secondary">Talk to us <ArrowRight /></Link>
            <div className="edx-work-proof"><b>30+</b><span>Selected products</span></div>
            <div className="edx-work-proof"><b>12+</b><span>Multi-industry builds</span></div>
            <div className="edx-work-proof"><b>100%</b><span>Full-stack delivery</span></div>
          </div>
        </div>

        <div className="edx-work-visual" aria-label={`Featured project: ${projects[activeProject].title}`}>
          <svg className="edx-work-visual-reference" viewBox="0 0 937 465" preserveAspectRatio="xMidYMid meet" role="img" aria-label="EDENOX technology platform displayed on a neon laptop with growth and solutions panels">
            <image href="/edenox-work-reference-exact.png" x="-735" y="0" width="1672" height="941" />
          </svg>
        </div>

        <div className="edx-work-projects" role="list" aria-label="Selected EDENOX work">
          {projects.map((project, index) => (
            <article
              key={project.title}
              role="listitem"
              tabIndex={0}
              className={`edx-work-project ${activeProject === index ? "is-active" : ""}`}
              style={{ "--project-accent": project.accent } as CSSProperties}
              onPointerEnter={() => setActiveProject(index)}
              onFocus={() => setActiveProject(index)}
              onClick={() => setActiveProject(index)}
              data-testid={`work-project-${index}`}
            >
              <div className="edx-work-project-topline">
                <b>{project.number}</b><span>{project.category}</span><small>VIEW CASE STUDY <ArrowRight /></small>
              </div>
              <ProjectPreview crop={project.crop} title={project.title} previewSrc={project.previewSrc} />
              <div className="edx-work-project-copy">
                <div><h3>{project.title}</h3><p>{project.description}</p></div>
                <a href={project.url} target="_blank" rel="noreferrer" aria-label={`Visit ${project.title} website`}><ArrowRight /></a>
              </div>
              <div className="edx-work-tags">
                {project.tags.map((tag) => <span key={tag}><CheckCircle2 />{tag}</span>)}
              </div>
            </article>
          ))}
        </div>

        <div className="edx-work-footerline">
          <span>TRUSTED BY FORWARD-THINKING ORGANISATIONS</span>
          <i />
          <span>Startups</span><span>Growing Businesses</span><span>Enterprises</span><span>Across Industries</span>
          <i />
          <b><ShieldCheck /> REAL IDEAS. REAL IMPACT.</b>
        </div>
      </div>
    </section>
  );
}
